// Text markups scale with zoom like a ground object: full size near the working
// zoom, shrinking 2x per zoom level below it (clamped so they stay legible at low
// zoom and don't balloon when zoomed in). Shared by the read-only display layer
// (MarkupLayer) and the editor (MarkupTextBox) so both behave identically.
export const TEXT_SCALE_REF_ZOOM = 18
export const TEXT_SCALE_MIN = 0.1
export const TEXT_SCALE_MAX = 4

export function textScaleForZoom(zoom: number | undefined): number {
  if (zoom == null || !Number.isFinite(zoom)) return 1
  const scale = 2 ** (zoom - TEXT_SCALE_REF_ZOOM)
  return Math.min(TEXT_SCALE_MAX, Math.max(TEXT_SCALE_MIN, scale))
}

// Mercator units one layout px of text covers on the ground. Exactly the same at every zoom
// where the text scales with the map, so derived positions only change outside that range.
export function textGroundPerPx(zoom: number): number {
  const scale = 2 ** (zoom - TEXT_SCALE_REF_ZOOM)
  if (scale >= TEXT_SCALE_MIN && scale <= TEXT_SCALE_MAX) {
    return 1 / (512 * 2 ** TEXT_SCALE_REF_ZOOM)
  }
  return textScaleForZoom(zoom) / (512 * 2 ** zoom)
}

// A text box's corners as mercator offsets from its anchor, for half-sizes hx, hy: the upright
// box's top-left, top-right, bottom-right and bottom-left, turned clockwise by the rotation
// (mercator y runs south). The GL quad and its hit test follow the same convention.
export function textBoxCorners(
  hx: number,
  hy: number,
  rotation: number,
): Array<[number, number]> {
  const r = (rotation * Math.PI) / 180
  const cos = Math.cos(r)
  const sin = Math.sin(r)
  return [
    [-hx, -hy],
    [hx, -hy],
    [hx, hy],
    [-hx, hy],
  ].map(([x, y]): [number, number] => [x * cos - y * sin, x * sin + y * cos])
}

// GL text is rastered once per integer zoom ("band"); outside the scale's range every
// band looks the same, so the bands stop there.
export const TEXT_BANDS = [14, 15, 16, 17, 18, 19, 20]

export function textBandOf(zoom: number): number {
  return Math.min(
    TEXT_BANDS[TEXT_BANDS.length - 1],
    Math.max(TEXT_BANDS[0], Math.floor(zoom)),
  )
}

// The band for text drawn `sizeScale` times its zoom's size: the zoom whose scale alone draws
// it that large, so the raster keeps pace with the quad. Exactly textBandOf at sizeScale 1.
export function textBandFor(zoom: number, sizeScale: number): number {
  return textBandOf(
    TEXT_SCALE_REF_ZOOM + Math.log2(sizeScale * textScaleForZoom(zoom)),
  )
}

// A band shows the scales from its zoom up to the next one; baking at their geometric
// mean keeps resampling within 1.41x either way.
export function textBandBake(band: number): number {
  return Math.sqrt(textScaleForZoom(band) * textScaleForZoom(band + 1))
}
